import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { TcsmAppConfigs } from '../../../../../../global/models/configuration-models';
import { DemographicsContext } from '../../../../../../global/models/demographics-model';
import { useAccountInfoSettings } from '../../../../../account-info/hooks/useAccountInfoSettings';
import { useAnnualEnrollmentSettings } from '../../../../../annual-enrollment/hooks/useAnnualEnrollmentSettings';
import { useDemographicsPerson } from '../../../../../account-info/hooks/useDemographicsPerson';
import { useSpouseInformation } from '../../../../../account-info/hooks/useSpouseInfo';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import GenericStepper from '../../../../../../global/components/navigation/generic-stepper';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../../annual-enrollment/models/annual-enrollment-models';
import { useDependents } from '../../../../../account-info/hooks/useDependents';
import { Person } from '../../../../../../global/models/person-model';
import { setCurrentNewHireBenefitEnrollmentStep, setCurrentNewHireEnrollmentStep } from '../../slice/new-hire-enrollment-slices';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { MemberCompensation, NewHireEnrollmentStep } from '../../models/new-hire-enrollment-models';
import { NewHireEnrollmentRoutes } from '../../models/new-hire-routes';
import { getMemberBenefitEnrollmentStepItems, getMemberBenefitEnrollmentSteps } from './helper/member-benefit-enrollment-helper';
import { useBenefitEnrollmentStep } from '../../hooks/useBenefitEnrollmentStep';
import { MemberCoverageEnrollment } from './member-coverage-enrollment';
import { withForbidden } from '../../../../../../global/components/HOCs/with-forbidden';
import { withLayout } from '../../../../../../global/components/HOCs/with-layout';
import { GpsType } from '../../../../../../global/components/layout/layout-models';
import { TcsmFeatures } from '../../../../../../global/models/features-model';
import AccountInfoContainer from '../../../../../account-info/components/account-info-container';
import { MemberRetirementPlans } from '../member-retirement-plans';
import { useBasePath } from '../../../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../../../account-info/constants/routes';
import { ArcRoutes } from '../../../../constants/routes';
import { useMemberPensionEnrollmentInfo } from '../hooks/useMemberPensionEnrollmentInfo';
import { useCurrentGhlFormId } from '../../hooks/useCurrentGhlFormId';
import { useMemberCoverageState } from '../../hooks/useMemberCoverageState';
import { useMemberEmployment } from '../hooks/useMemberEmployment';
import { useMemberCompensation } from '../hooks/useMemberCompensation';
import { Employment } from '../../../employment/employment-model';
import { PensionEnrollmentInfo } from '../../../pension-enrollment/models/pension-enrollment-model';
import { MemberNewHireEnrollmentCoverage } from '../models/member-coverage-model';
import { isNepEnrollment } from '../../hooks/isNepEnrollment';

const MemberRetirementPlansWrapper = withLayout(null, GpsType.MEMBER_NEW_HIRE_RETIREMENT_PLAN)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));

interface Props {
	settings: TcsmAppConfigs;
}

export function MemberBenefitEnrollment(props: Props) {

	const { settings } = props;
	const app = useContext(DemographicsContext);
	const routeMatch = useRouteMatch();
	const demographicsSettingsLoaded = useAccountInfoSettings(settings.personalInfoSettingId, settings.relationshipsSettingId);
	const aeSettingsLoaded = useAnnualEnrollmentSettings(settings.annualEnrollmentSettingsId);
	const personLoaded = useDemographicsPerson(app);
	const currentPerson = useSelector<TcsmStates, Person>(state => state?.personalInfo[app]?.currentPerson);
	const spousesLoaded = useSpouseInformation(currentPerson?.person_guid);
	const dependentsLoaded = useDependents(currentPerson?.person_guid);
	const dispatch = useDispatch();
	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);

	const newHireBenefitsDefault = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${ArcRoutes.MemberNewHireEnrollment}${NewHireEnrollmentRoutes.BenefitEnrollment}${NewHireEnrollmentRoutes.Retirement}`);

	const guid = useSelector<TcsmStates, string>(state => state.authenticatedState?.clientGuid);
	const currentForm = useCurrentGhlFormId();	
	const memberCoverageLoaded = useMemberCoverageState(guid, currentForm?.id);	
    const memberEmploymentLoaded = useMemberEmployment();
    const memberCompensationLoaded = useMemberCompensation();
    const memberPensionEnrollmentInfoLoaded = useMemberPensionEnrollmentInfo();	
	
    const memberEmployment = useSelector<TcsmStates, Employment>(state => state?.newHireEnrollmentState?.memberEmployment);
    const memberCompensationState = useSelector<TcsmStates, MemberCompensation>(state => state?.newHireEnrollmentState?.memberCompensation);	
	const memberCoverageState = useSelector<TcsmStates, MemberNewHireEnrollmentCoverage>(state => state.newHireEnrollmentState?.memberCoverageState);
	const memberPensionEnrollmentInfo = useSelector<TcsmStates, PensionEnrollmentInfo>(state => state?.newHireEnrollmentState?.memberPensionInfo);	
	const isNep = isNepEnrollment();

	useEffect(() => {
		dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.BENEFIT_ENROLLMENT));
	}, []);

	if ((!memberPensionEnrollmentInfoLoaded || !memberEmploymentLoaded || !memberCompensationLoaded) && !isNep) {
		return <LoadingCircle />;

	}

	if (!demographicsSettingsLoaded || !personLoaded || !aeSettingsLoaded || !spousesLoaded || !dependentsLoaded || !memberCoverageLoaded ) {
		return <LoadingCircle />;
	}

	return (
		<>
			<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.newHireEnrollment}>
				<GenericStepper
					basePath={routeMatch.path}
					allSteps={getMemberBenefitEnrollmentSteps(memberCoverageState, memberCompensationState, memberEmployment, memberPensionEnrollmentInfo)}
					useCurrentStep={useBenefitEnrollmentStep}
					setCurrentStep={setCurrentNewHireBenefitEnrollmentStep}
					progressItems={getMemberBenefitEnrollmentStepItems(newHireProps, memberCoverageState, memberCompensationState, memberEmployment, memberPensionEnrollmentInfo)}
				/>
				<Switch>
					<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Coverage}`} render={() => <MemberCoverageEnrollment basePath={routeMatch.path} />} />
					<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Review}`} render={() => <MemberCoverageEnrollment basePath={routeMatch.path} />} />
					<Route exact path={`${routeMatch.path}${NewHireEnrollmentRoutes.Retirement}`} render={() => (
						<MemberRetirementPlansWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MemberRetirementPlans basePath={routeMatch.path} />						
						</MemberRetirementPlansWrapper>
					)} />
					<Route path={`${routeMatch.path}/*`}>
						<Redirect to={newHireBenefitsDefault} />
					</Route>	
				</Switch>
			</AnnualEnrollmentContext.Provider >
		</>
	);
}